import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { BulkReprocessRunDtoStatus, BulkReprocessRunDtoMode } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';
import { PageHeader } from '../../components/page-header';
import { BulkReprocessRunsList, MODE_METADATA } from '../../contexts/admin';
import type { BulkReprocessRunsSearch } from '../../router/tree';

const ALL = 'ALL';

function titleCase(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

export function BulkReprocessRunsPage() {
  const navigate = useNavigate();
  // Loose-mode read: this page is only mounted under `/admin/bulk-replay`
  // (same pattern as the launch form's prefill read).
  const search = useSearch({ strict: false }) as Partial<BulkReprocessRunsSearch>;
  const page = search.page ?? 1;

  function patch(next: Partial<BulkReprocessRunsSearch>) {
    // Build the target search explicitly from the (loose-read) current values
    // rather than the navigate updater's `prev` — under strict:false `prev`
    // widens to the union of every route's search, which doesn't narrow here.
    const merged: BulkReprocessRunsSearch = {
      status: search.status,
      mode: search.mode,
      page: search.page,
      ...next,
    };
    void navigate({ to: '/admin/bulk-replay', search: merged });
  }

  return (
    <div className="flex flex-1 flex-col gap-8">
      <PageHeader
        title="Bulk Replay"
        description="Staff-only tool to re-run post-call processing, replay from scratch, or re-send webhooks across a batch of interview sessions."
        action={
          <Button
            type="button"
            variant="tinted"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate({ to: '/admin/bulk-replay/new' })}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.8} />
            Create bulk replay
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={search.status ?? ALL}
          onValueChange={(v) =>
            patch({
              status: v === ALL ? undefined : (v as BulkReprocessRunsSearch['status']),
              page: 1,
            })
          }
        >
          <SelectTrigger className="h-9 w-[170px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {Object.values(BulkReprocessRunDtoStatus).map((s) => (
              <SelectItem key={s} value={s}>
                {titleCase(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={search.mode ?? ALL}
          onValueChange={(v) =>
            patch({ mode: v === ALL ? undefined : (v as BulkReprocessRunsSearch['mode']), page: 1 })
          }
        >
          <SelectTrigger className="h-9 w-[190px]">
            <SelectValue placeholder="Mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All modes</SelectItem>
            {Object.values(BulkReprocessRunDtoMode).map((m) => (
              <SelectItem key={m} value={m}>
                {MODE_METADATA[m].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(search.status || search.mode) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => patch({ status: undefined, mode: undefined, page: 1 })}
          >
            Clear filters
          </Button>
        )}
      </div>

      <BulkReprocessRunsList
        params={{ status: search.status, mode: search.mode, page }}
        onSelectRun={(runId) => navigate({ to: '/admin/bulk-replay/$runId', params: { runId } })}
        onPageChange={(p) => patch({ page: p })}
      />
    </div>
  );
}
